import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LandPlot, Loader2 } from 'lucide-react';

import type { Asset } from '../../types/domain';
import type { AssetParticleExtractionLiveEventStatus } from '../../services/liveUpdatesService';
import {
  buildAssetListSummary,
  getAssetTitle,
  getParticlePreviewTitle,
  isAssetExtractionActive,
  isAssetExtractionFailed,
} from '../../utils/assetDisplay';
import { formatShortDateLabel, formatDayKey, isImageLikeAsset, resolveSubjectBadge } from '../../utils/assetMedia';
import { isPasAssetType, summarizePasWorkbenchForCard } from '../../utils/pasAsset';
import { ProxyImage } from './ProxyImage';

type AssetCardProps = {
  asset: Asset;
  particleCount?: number;
  particlePreview?: string;
  /** When false, suppress failure UI until counts are hydrated (home feed). */
  particleCountKnown?: boolean;
  extractionStatus?: AssetParticleExtractionLiveEventStatus | null;
  highlighted?: boolean;
  to: string;
  onNavigate?: () => void;
};

export function AssetCard({
  asset,
  particleCount = 0,
  particlePreview,
  particleCountKnown = true,
  extractionStatus,
  highlighted = false,
  to,
  onNavigate,
}: AssetCardProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const isPas = isPasAssetType(asset.type);
  const pasSummary = isPas
    ? summarizePasWorkbenchForCard(asset.metadata as Record<string, unknown> | null | undefined)
    : null;
  const extractionActive = !isPas && (
    extractionStatus === 'processing' || isAssetExtractionActive(asset)
  );
  const extractionFailed = !isPas
    && particleCountKnown
    && !extractionActive
    && particleCount === 0
    && (
      extractionStatus === 'failed'
      || (extractionStatus == null && isAssetExtractionFailed(asset, { particleCount }))
    );
  const particleCountPending = !isPas && !particleCountKnown;
  const fallbackTitle = getAssetTitle(asset, particlePreview);
  const title = pasSummary?.hatchTitle
    || (isPas
      ? (pasSummary && pasSummary.practiceNodeCount > 0
        ? `实战标注 · ${pasSummary.practiceNodeCount} 处`
        : (fallbackTitle === '知识素材' ? '实战答题纸' : fallbackTitle))
      : fallbackTitle);
  const summary = isPas
    ? null
    : buildAssetListSummary(asset, { particlePreview, particleCount, particleCountKnown });
  const subjectBadge = resolveSubjectBadge(asset);
  const displayCount = isPas ? (pasSummary?.practiceItemCount ?? 0) : particleCount;
  const footerMeta = extractionActive
    ? '知识颗粒提取中'
    : extractionFailed
      ? '需要重新提取'
      : formatShortDateLabel(formatDayKey(asset.created_at));

  return (
    <Link
      id={`asset-card-${asset.id}`}
      to={to}
      state={{ from: `${location.pathname}${location.search}` }}
      onClick={(event) => {
        event.preventDefault();
        onNavigate?.();
        navigate(to);
      }}
      className={`group block overflow-hidden rounded-md bg-surface transition ${
        highlighted ? 'ring-2 ring-primary/70' : ''
      } ${extractionFailed ? 'ring-1 ring-danger/30' : ''}`}
    >
      <div className="relative aspect-[4/5] overflow-hidden">
        {isImageLikeAsset(asset) ? (
          <ProxyImage
            rawUrl={asset.url}
            ossId={asset.oss_id}
            alt={title}
            className="h-full w-full transition duration-300 group-hover:scale-[1.02]"
            imgClassName="object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-[#EDF1F7] text-sm text-subtle">
            {asset.type || '素材'}
          </div>
        )}
        {isPas ? (
          <span
            className="absolute left-2 top-2 flex h-7 w-7 items-center justify-center rounded-lg border border-border bg-white/90 shadow-sm"
            title="实战答题纸"
            aria-label="实战答题纸"
          >
            <LandPlot size={14} className="text-primary-strong" strokeWidth={2.2} />
          </span>
        ) : null}
        {subjectBadge ? (
          <span
            className={`absolute flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold text-white shadow-sm ${
              isPas ? 'bottom-2 right-2' : 'left-2 top-2'
            }`}
            style={{ backgroundColor: subjectBadge.color }}
          >
            {subjectBadge.char}
          </span>
        ) : null}
      </div>

      <div className="space-y-2 px-2.5 py-2.5">
        <div className={`line-clamp-2 text-sm font-semibold leading-snug ${extractionFailed ? 'text-danger' : 'text-ink'}`}>
          {title}
        </div>
        <div className="flex items-center justify-between gap-2 text-xs">
          <span className={`truncate ${extractionFailed ? 'text-danger/80' : 'text-subtle'}`}>{footerMeta}</span>
          <span
            className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 font-medium ${
              extractionActive || particleCountPending
                ? 'bg-primary/10 text-primary'
                : extractionFailed
                  ? 'bg-danger/10 text-danger'
                  : 'bg-[#EAF2FF] text-primary-strong'
            }`}
            title={isPas ? '刻意练习变式数' : '知识颗粒数'}
          >
            {extractionActive ? (
              <>
                <Loader2 size={12} className="animate-spin" />
                提取中
              </>
            ) : particleCountPending ? (
              <>
                <Loader2 size={12} className="animate-spin" />
                ⬢ …
              </>
            ) : isPas ? (
              <>
                <LandPlot size={12} strokeWidth={2.2} />
                {displayCount}
              </>
            ) : (
              <>⬢ {displayCount}</>
            )}
          </span>
        </div>
        {summary && summary !== title ? (
          <p className="line-clamp-2 text-xs text-muted">{summary}</p>
        ) : null}
      </div>
    </Link>
  );
}

export function buildParticlePreviewFromParticles(
  particles: Array<{ title?: string; questions?: unknown }>,
  options?: { softenLatexForLanguageCard?: boolean },
) {
  return getParticlePreviewTitle(particles, options);
}
